import React, { useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { map } from 'lodash'
import { UploadCloud } from 'lucide-react'

import { Dropzone, DropzoneContent } from '@/components/ui/dropzone'
import { isArchiveFile, timeout } from '@/utils'
import { EArchiveExtension, EDocumentExtension } from '@/utils/constants'

import { extractArchive } from '../extractFileUtils'
import { useFileUploadStore } from '../store'
import { UploadPhase } from '../types'

const DELAY_EXTRACT_ARCHIVE_MS = 200

export const FileUploadDropzone: React.FC<{
  className?: string
  disabled?: boolean
}> = ({ className = '', disabled = false }) => {
  const { t } = useTranslation()
  const {
    selectedDeliverableSet,
    addFiles,
    addErrorFiles,
    setUploadPhase,
    contextDeliverableSetId,
  } = useFileUploadStore()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  // Handle file drop/selection
  const handleFileDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    // Prepare files array for validation process
    const files: File[] = []

    const archiveFiles = acceptedFiles.filter((file) =>
      isArchiveFile(file.name)
    )
    const nonArchiveFiles = acceptedFiles.filter(
      (file) => !isArchiveFile(file.name)
    )

    if (archiveFiles.length > 0) {
      setUploadPhase(UploadPhase.UNZIPPING)
      await timeout(DELAY_EXTRACT_ARCHIVE_MS)

      for (const archiveFile of archiveFiles) {
        const { files: extractedFiles, errors } =
          await extractArchive(archiveFile)

        if (errors.length) addErrorFiles(errors)
        if (extractedFiles.length === 0) continue

        files.push(...extractedFiles)
      }
    }

    files.push(...nonArchiveFiles, ...map(fileRejections, 'file'))

    setUploadPhase(UploadPhase.VALIDATION)

    // Pass deliverable set ID to addFiles for API validation
    const deliverableSetId =
      selectedDeliverableSet?.id || contextDeliverableSetId || undefined
    addFiles(files, deliverableSetId)

    setSelectedFile(null)

    let file = acceptedFiles[0]
    setTimeout(() => {
      if (fileRejections.length > 0) file = fileRejections[0].file
      setSelectedFile(file)
    })
  }

  // Handle file drop errors
  const handleDropError = (_error: Error) => {
    // Error is already handled in handleFileDrop via fileRejections
    // This is a fallback for other error types
  }

  return (
    <Dropzone
      accept={{
        'text/html': [EDocumentExtension.HTML, EDocumentExtension.HTM],
        'application/pdf': [EDocumentExtension.PDF],
        'application/zip': [EArchiveExtension.ZIP],
        'application/x-7z-compressed': [EArchiveExtension.SEVEN_ZIP],
      }}
      onDrop={handleFileDrop}
      onError={handleDropError}
      src={selectedFile ? [selectedFile] : []}
      disabled={disabled}
      maxFiles={0}
      className={className}
    >
      <DropzoneContent className="gap-4 px-6 py-4">
        <div className="flex items-center justify-center size-10 p-2.5 bg-white border border-solid border-gray-light-200 rounded-lg">
          <UploadCloud size={20} className="text-secondary-700" />
        </div>
        <div className="text-sm font-normal text-center">
          <p className="m-0 text-base">
            <span className="text-sm font-semibold cursor-pointer text-brand-600 hover:text-brand-700">
              {t('fileUpload.dropzone.clickToUpload')}
            </span>
            <span className="text-sm font-normal text-tertiary-600">
              {t('fileUpload.dropzone.orDragAndDrop')}
            </span>
          </p>
          <p className="m-0 text-sm text-tertiary-600">
            {t('fileUpload.dropzone.supportedFormats')}
          </p>
        </div>
      </DropzoneContent>
    </Dropzone>
  )
}
